import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/session.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { LocalStorageService } from "src/app/storage/local-storage.service";
import { UtilService } from "src/app/util/util.service";
import { FlowsService } from "../flows.service";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

@Component({
  selector: "app-report",
  templateUrl: "./report.component.html",
  styleUrls: ["./report.component.scss"],
  animations: [
    trigger("detailExpand", [
      state("collapsed", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition(
        "expanded <=> collapsed",
        animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)"),
      ),
    ]),
  ],
})
export class ReportComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  report: any;
  displayedColumns = ["date", "total"];
  reportDays!: any[];
  reportDataSource!: MatTableDataSource<any>;
  expandedElement: any;
  perPage: number = 20;

  @ViewChild("reportTable") reportTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  filterForm!: FormGroup;
  onComponentDestroy = new Subject();
  conversionsChartOptions!: {
    grid: { left: number; top: number; right: number; bottom: number };
    xAxis: {
      type: string;
      boundaryGap: boolean;
      axisLabel: {
        color: string;
        hideOverlap: boolean;
        formatter: (d: any) => string;
      };
      axisLine: { show: boolean };
      axisTick: { show: boolean };
    };
    yAxis: { type: string; splitLine: { show: boolean }; show: boolean };
    tooltip: {
      trigger: string;
      axisPointer: { type: string };
      formatter: (params: any, ticket: any, callback: any) => string;
    };
    series: {
      data: any;
      showSymbol: boolean;
      label: { color: string };
      type: string;
      itemStyle: { color: string; borderRadius: number[] };
      large: boolean;
      barMaxWidth: string;
    }[];
  };
  conversions: number = 0;
  constructor(
    private route: ActivatedRoute,
    private flowsService: FlowsService,
    private sessionService: SessionService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;

    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.initData(this.route.snapshot.data.report);
    this.filterForm.valueChanges
      .pipe(
        takeUntil(this.onComponentDestroy),
        filter(
          () =>
            this.filterForm.valid &&
            this.filterForm.controls.startDate.value <
              this.filterForm.controls.endDate.value,
        ),
        switchMap(() =>
          this.flowsService.getReport(
            this.sessionService.currentAccountValue!.accountID,
            this.report.id,
            {
              startDate: this.filterForm.controls.startDate.value,
              endDate: this.filterForm.controls.endDate.value,
            },
          ),
        ),
      )
      .subscribe((report) => {
        this.initData(report);
      });
  }

  ngAfterViewInit() {
    this.reportTable?.renderRows();
    this.reportDataSource.sort = this.sort;
    this.reportDataSource.paginator = this.paginator;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  initData(report: any) {
    this.report = report;
    this.reportDays = [];

    let conversions = 0;

    this.report.conversions.forEach(
      (date: any) => (conversions += date.conversions),
    );

    this.conversions = conversions;

    this.initConversionChart();

    for (let signal of this.report.signals) {
      let sendDate = signal.sent_on_date ?? signal.created_at;
      sendDate = new Date(new Date(sendDate).toDateString());

      const date = this.reportDays.find(
        (day) => day.date.getTime() === sendDate.getTime(),
      );
      if (date) {
        date.signals.push(signal);
        date.total = date.total + 1;
      } else {
        this.reportDays.push({
          date: sendDate,
          signals: [signal],
          total: 1,
        });
      }
    }

    this.reportDataSource = new MatTableDataSource(this.reportDays);

    this.reportTable?.renderRows();
    this.reportDataSource.sort = this.sort;
    this.reportDataSource.paginator = this.paginator;
  }

  initConversionChart() {
    let type: "Day" | "Week" | "Month";

    if (this.report.conversions.length < 90) {
      type = "Day";
    } else if (this.report.conversions.length < 180) {
      type = "Week";
    } else {
      type = "Month";
    }

    const conversionDataObj: {
      type: "Day" | "Week" | "Month";
      data: {
        date: Date;
        total: number;
      }[];
    } = { type, data: [] };

    if (type === "Day") {
      for (let dateRange of this.report.conversions) {
        conversionDataObj.data.push({
          date: dateRange.date,
          total: dateRange.conversions,
        });
      }
    } else if (type === "Week") {
      this.report.conversions.sort((a: any, b: any) => {
        return (a.date as string).localeCompare(b.date);
      });

      const minDate: Date = new Date(this.report.conversions[0].date);
      const maxDate = new Date(
        this.report.conversions[this.report.conversions.length - 1].date,
      );

      let currStartWeek = minDate;
      while (currStartWeek.getTime() < maxDate.getTime()) {
        const weekEnd = new Date(currStartWeek);

        weekEnd.setDate(currStartWeek.getDate() + 7);

        const weekObj = {
          weekStart: currStartWeek,
          weekEnd,
          total: 0,
        };

        const conversionsInRange = this.report.conversions.filter(
          (conversion: any) => {
            const date = new Date(conversion.date);
            return (
              date.getTime() >= weekObj.weekStart.getTime() &&
              date.getTime() < weekObj.weekEnd.getTime()
            );
          },
        );

        conversionsInRange.forEach((conversion: any) => {
          weekObj.total = weekObj.total + conversion.conversions;
        });

        conversionDataObj.data.push({
          date: weekObj.weekStart,
          total: weekObj.total,
        });

        currStartWeek = weekEnd;
      }
    } else if (type === "Month") {
      this.report.conversions.sort((a: any, b: any) => {
        return (a.date as string).localeCompare(b.date);
      });

      const minDate = new Date(this.report.conversions[0].date);

      const maxDate = new Date(
        this.report.conversions[this.report.conversions.length - 1].date,
      );

      let currMonth = minDate;

      while (currMonth.getTime() < maxDate.getTime()) {
        const monthEnd = new Date(currMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(1);
        monthEnd.setHours(0);
        monthEnd.setMinutes(0);
        monthEnd.setSeconds(0);
        monthEnd.setMilliseconds(0);

        const monthObj = {
          month: currMonth,
          total: 0,
        };

        const conversionsInRange = this.report.conversions.filter(
          (conversion: any) => {
            const date = new Date(conversion.date);
            return (
              date.getTime() >= monthObj.month.getTime() &&
              date.getTime() < monthEnd.getTime()
            );
          },
        );

        conversionsInRange.forEach((conversion: any) => {
          monthObj.total = monthObj.total + conversion.conversions;
        });

        conversionDataObj.data.push({
          date: monthObj.month,
          total: monthObj.total,
        });

        currMonth = monthEnd;
      }
    }

    this.conversionsChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 22,
      },

      xAxis: {
        type: "category",
        boundaryGap: false,

        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type, params[0].axisValue);
        },
      },
      series: [
        {
          data: conversionDataObj.data.map((data: any) => {
            return [data.date, data.total];
          }),
          showSymbol: false,

          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "40%",
        },
      ],
    };
  }

  getXLabel(dateString: string, type: "Day" | "Week" | "Month") {
    const date = new Date(dateString);
    if (type === "Day") {
      return this.getDayLabel(date);
    } else if (type === "Week") {
      return this.getWeekLabel(date);
    } else if (type === "Month") {
      return this.getMonthLabel(date);
    }

    return "";
  }

  getDayLabel(date: Date) {
    if (date.getDate() === 1) {
      return MONTHS[date.getMonth()];
    }

    return date.getDate().toString();
  }

  getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  getTooltip(params: any, type: "Day" | "Week" | "Month", dateString: string) {
    const date = new Date(dateString);

    if (type === "Day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    }
    return "";
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }
}
